/**
 * Extracts a face region from an image using bounding box values and returns a JPG File.
 * @param {string} imageUrl - The source image URL or base64 string.
 * @param {{left: number, top: number, width: number, height: number}} box - Bounding box values (pixel values).
 * @returns {Promise<File>} - Returns a JPG image File of the cropped face region.
 */
export async function extractFaceFromImage(
  imageUrl: string,
  box: { left: number; top: number; width: number; height: number },
  fileName: string = 'face.jpg'
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    // Set crossOrigin only if the imageUrl is not a data URL or blob URL
    if (!imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = function () {
      // Convert AWS Rekognition ratios to pixel values
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      // Padding as a percentage of the largest box dimension (e.g., 12%)
      const PADDING_RATIO = 0.12;
      const boxW = (box.width || 0) * iw;
      const boxH = (box.height || 0) * ih;
      const pad = Math.round(Math.max(boxW, boxH) * PADDING_RATIO);
      let sx = Math.round((box.left || 0) * iw) - pad;
      let sy = Math.round((box.top || 0) * ih) - pad;
      let sw = Math.round(boxW) + pad * 2;
      let sh = Math.round(boxH) + pad * 2;
      // Clamp to image bounds
      if (sx < 0) {
        sw += sx;
        sx = 0;
      }
      if (sy < 0) {
        sh += sy;
        sy = 0;
      }
      if (sx + sw > iw) sw = iw - sx;
      if (sy + sh > ih) sh = ih - sy;
      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('No canvas context');
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      canvas.toBlob(blob => {
        if (!blob) return reject('Failed to create blob');
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        resolve(file);
      }, 'image/jpeg');
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
}
